/**
 * Curated catalog of "Explora México" destinations.
 *
 * Hybrid classification (curated base + keyword reinforcement + AI enrichment):
 * - geographic coordinates (lat, lng) used by MexicoMap to position markers
 * - base categories (curated, never inferred only from AI)
 * - keyword reinforcement: tour titles/locations matching these terms
 *   reinforce the destination's tour count and "real tours" recommendation
 * - reusable hero image + bilingual description for the destination drawer
 */

import destOaxaca from "@/assets/dest-oaxaca.webp";
import destMerida from "@/assets/dest-merida.webp";
import destYucatan from "@/assets/dest-yucatan.webp";
import destCabo from "@/assets/dest-cabo.webp";
import destCdmx from "@/assets/dest-cdmx.webp";
import destTulum from "@/assets/dest-tulum.webp";
import destBacalar from "@/assets/dest-bacalar.webp";
import destHolbox from "@/assets/dest-holbox.webp";
import destPlaya from "@/assets/dest-playa.webp";
import destCozumel from "@/assets/dest-cozumel.webp";

// Unique per-destination photography (CDN-hosted).
import destCancunAsset from "@/assets/destinations/cancun.jpg.asset.json";
import destChichenAsset from "@/assets/destinations/chichen-itza.jpg.asset.json";
import destIslaMujeresAsset from "@/assets/destinations/isla-mujeres.jpg.asset.json";
import destPuertoVallartaAsset from "@/assets/destinations/puerto-vallarta.jpg.asset.json";
import destPlayaDelCarmenAsset from "@/assets/destinations/playa-del-carmen.jpg.asset.json";
import destTeotihuacanAsset from "@/assets/destinations/teotihuacan.jpg.asset.json";
import destGuanajuatoAsset from "@/assets/destinations/guanajuato.jpg.asset.json";
import destSMAAsset from "@/assets/destinations/san-miguel-de-allende.jpg.asset.json";
import destPalenqueAsset from "@/assets/destinations/palenque.jpg.asset.json";
import destValladolidAsset from "@/assets/destinations/valladolid.jpg.asset.json";
import destCelestunAsset from "@/assets/destinations/celestun.jpg.asset.json";
import destLasColoradasAsset from "@/assets/destinations/las-coloradas.jpg.asset.json";
import destRioLagartosAsset from "@/assets/destinations/rio-lagartos.jpg.asset.json";
import destIzamalAsset from "@/assets/destinations/izamal.jpg.asset.json";
import destCalakmulAsset from "@/assets/destinations/calakmul.jpg.asset.json";
import destSisalAsset from "@/assets/destinations/sisal.jpg.asset.json";
import destMahahualAsset from "@/assets/destinations/mahahual.jpg.asset.json";
import destElCuyoAsset from "@/assets/destinations/el-cuyo.jpg.asset.json";
import destManiAsset from "@/assets/destinations/mani.jpg.asset.json";
import destXpujilAsset from "@/assets/destinations/xpujil.jpg.asset.json";

const destCancun = destCancunAsset.url;
const destChichen = destChichenAsset.url;
const destIslaMujeres = destIslaMujeresAsset.url;
const destPuertoVallarta = destPuertoVallartaAsset.url;
const destPlayaDelCarmen = destPlayaDelCarmenAsset.url;
const destTeotihuacan = destTeotihuacanAsset.url;
const destGuanajuato = destGuanajuatoAsset.url;
const destSMA = destSMAAsset.url;
const destPalenque = destPalenqueAsset.url;
const destValladolid = destValladolidAsset.url;
const destCelestun = destCelestunAsset.url;
const destLasColoradas = destLasColoradasAsset.url;
const destRioLagartos = destRioLagartosAsset.url;
const destIzamal = destIzamalAsset.url;
const destCalakmul = destCalakmulAsset.url;
const destSisal = destSisalAsset.url;
const destMahahual = destMahahualAsset.url;
const destElCuyo = destElCuyoAsset.url;
const destMani = destManiAsset.url;
const destXpujil = destXpujilAsset.url;

export type ExploraCategory =
  | "playa"
  | "cultura"
  | "naturaleza"
  | "aventura"
  | "islas"
  | "arqueologia"
  | "gastronomia"
  | "cenotes"
  | "cultura-maya"
  | "vida-nocturna"
  | "familiar"
  | "romance";

export interface ExploraDestination {
  slug: string;
  name: string;
  state: string;
  lat: number;
  lng: number;
  /** Slug used for /tours/$city route. */
  citySlug: string;
  image: string;
  categories: ExploraCategory[];
  /** Substrings (accent-folded, lowercased) used to reinforce tour matching. */
  keywords: string[];
  description: { es: string; en: string };
}

export const EXPLORA_DESTINATIONS: ExploraDestination[] = [
  {
    slug: "cancun",
    name: "Cancún",
    state: "Quintana Roo",
    lat: 21.16,
    lng: -86.85,
    citySlug: "cancun",
    image: destCancun,
    categories: ["playa", "aventura", "gastronomia", "vida-nocturna", "familiar"],
    keywords: ["cancun", "cancún", "zona hotelera"],
    description: {
      es: "Caribe turquesa, vida nocturna vibrante y la puerta a la Riviera Maya.",
      en: "Turquoise Caribbean waters, vibrant nightlife and the gateway to the Riviera Maya.",
    },
  },
  {
    slug: "tulum",
    name: "Tulum",
    state: "Quintana Roo",
    lat: 20.21,
    lng: -87.46,
    citySlug: "tulum",
    image: destTulum,
    categories: ["playa", "arqueologia", "cultura", "cenotes", "cultura-maya", "romance"],
    keywords: ["tulum"],
    description: {
      es: "Ruinas mayas frente al mar, cenotes secretos y playas de ensueño.",
      en: "Maya ruins by the sea, hidden cenotes and dreamlike beaches.",
    },
  },
  {
    slug: "playa-del-carmen",
    name: "Playa del Carmen",
    state: "Quintana Roo",
    lat: 20.63,
    lng: -87.07,
    citySlug: "playa-del-carmen",
    image: destPlayaDelCarmen,
    categories: ["playa", "gastronomia", "aventura", "vida-nocturna", "cenotes"],
    keywords: ["playa del carmen", "5ta avenida", "quinta avenida"],
    description: {
      es: "Quinta Avenida, snorkel en arrecifes y trampolín hacia Cozumel.",
      en: "La Quinta Avenida, reef snorkel and a springboard to Cozumel.",
    },
  },
  {
    slug: "cozumel",
    name: "Cozumel",
    state: "Quintana Roo",
    lat: 20.42,
    lng: -86.92,
    citySlug: "cozumel",
    image: destCozumel,
    categories: ["islas", "playa", "naturaleza", "aventura", "familiar"],
    keywords: ["cozumel"],
    description: {
      es: "La meca del buceo en arrecifes del Caribe, con Palancar y Cielito.",
      en: "Caribbean reef diving paradise, home of Palancar and Cielito.",
    },
  },
  {
    slug: "isla-mujeres",
    name: "Isla Mujeres",
    state: "Quintana Roo",
    lat: 21.23,
    lng: -86.73,
    citySlug: "isla-mujeres",
    image: destIslaMujeres,
    categories: ["islas", "playa", "naturaleza", "romance", "familiar"],
    keywords: ["isla mujeres"],
    description: {
      es: "Playa Norte de aguas cristalinas, ballena tiburón y atardeceres únicos.",
      en: "Crystal-clear Playa Norte, whale shark season and unforgettable sunsets.",
    },
  },
  {
    slug: "holbox",
    name: "Holbox",
    state: "Quintana Roo",
    lat: 21.52,
    lng: -87.38,
    citySlug: "holbox",
    image: destHolbox,
    categories: ["islas", "naturaleza", "playa", "romance"],
    keywords: ["holbox"],
    description: {
      es: "Isla virgen sin coches, flamencos, bioluminiscencia y arena fina.",
      en: "Car-free island with flamingos, bioluminescence and powder-soft sand.",
    },
  },
  {
    slug: "bacalar",
    name: "Bacalar",
    state: "Quintana Roo",
    lat: 18.68,
    lng: -88.39,
    citySlug: "bacalar",
    image: destBacalar,
    categories: ["naturaleza", "aventura", "romance"],
    keywords: ["bacalar", "laguna de los siete colores"],
    description: {
      es: "La laguna de los siete colores y los estromatolitos vivos más antiguos.",
      en: "The lagoon of seven colors and the oldest living stromatolites.",
    },
  },
  {
    slug: "chichen-itza",
    name: "Chichén Itzá",
    state: "Yucatán",
    lat: 20.68,
    lng: -88.57,
    citySlug: "chichen-itza",
    image: destChichen,
    categories: ["arqueologia", "cultura", "cultura-maya", "familiar"],
    keywords: ["chichen", "chichén", "chichen itza", "chichén itzá"],
    description: {
      es: "Maravilla del mundo maya: la pirámide de Kukulcán y el cenote sagrado.",
      en: "A wonder of the Maya world: Kukulcán pyramid and the sacred cenote.",
    },
  },
  {
    slug: "valladolid",
    name: "Valladolid",
    state: "Yucatán",
    lat: 20.69,
    lng: -88.2,
    citySlug: "valladolid",
    image: destValladolid,
    categories: ["cultura", "gastronomia", "naturaleza", "cenotes", "cultura-maya"],
    keywords: ["valladolid"],
    description: {
      es: "Pueblo Mágico con cenotes secretos y gastronomía yucateca de raíz.",
      en: "Pueblo Mágico with hidden cenotes and deep-rooted Yucatecan cuisine.",
    },
  },
  {
    slug: "merida",
    name: "Mérida",
    state: "Yucatán",
    lat: 20.97,
    lng: -89.62,
    citySlug: "merida",
    image: destMerida,
    categories: ["cultura", "gastronomia", "cultura-maya", "familiar"],
    keywords: ["merida", "mérida"],
    description: {
      es: "Capital blanca de Yucatán, haciendas henequeneras y ruta Puuc.",
      en: "The white capital of Yucatán, henequen haciendas and the Puuc route.",
    },
  },
  {
    slug: "oaxaca",
    name: "Oaxaca",
    state: "Oaxaca",
    lat: 17.06,
    lng: -96.72,
    citySlug: "oaxaca",
    image: destOaxaca,
    categories: ["cultura", "gastronomia", "familiar"],
    keywords: ["oaxaca", "mezcal", "monte alban", "monte albán"],
    description: {
      es: "Mezcal, mole y los pueblos artesanos más vivos de México.",
      en: "Mezcal, mole and the most vibrant artisan villages in Mexico.",
    },
  },
  {
    slug: "cdmx",
    name: "Ciudad de México",
    state: "CDMX",
    lat: 19.43,
    lng: -99.13,
    citySlug: "cdmx",
    image: destCdmx,
    categories: ["cultura", "gastronomia", "vida-nocturna", "familiar"],
    keywords: ["ciudad de mexico", "ciudad de méxico", "cdmx", "mexico city"],
    description: {
      es: "Megalópolis con museos icónicos, mercados y rooftops legendarios.",
      en: "Megacity of iconic museums, markets and legendary rooftops.",
    },
  },
  {
    slug: "los-cabos",
    name: "Los Cabos",
    state: "Baja California Sur",
    lat: 22.89,
    lng: -109.9,
    citySlug: "los-cabos",
    image: destCabo,
    categories: ["playa", "naturaleza", "aventura", "romance", "vida-nocturna"],
    keywords: ["cabo", "los cabos", "san jose del cabo", "cabo san lucas"],
    description: {
      es: "El Arco, ballenas grises y desierto frente al Pacífico.",
      en: "El Arco, gray whales and the desert meeting the Pacific.",
    },
  },
  {
    slug: "puerto-vallarta",
    name: "Puerto Vallarta",
    state: "Jalisco",
    lat: 20.65,
    lng: -105.23,
    citySlug: "puerto-vallarta",
    image: destPuertoVallarta,
    categories: ["playa", "gastronomia", "aventura", "romance", "familiar"],
    keywords: ["vallarta", "puerto vallarta", "yelapa"],
    description: {
      es: "Malecón, Sierra Madre y playas vírgenes en la Bahía de Banderas.",
      en: "Malecón, Sierra Madre and pristine beaches in Banderas Bay.",
    },
  },
  {
    slug: "palenque",
    name: "Palenque",
    state: "Chiapas",
    lat: 17.51,
    lng: -92.06,
    citySlug: "palenque",
    image: destPalenque,
    categories: ["arqueologia", "naturaleza", "cultura", "cultura-maya", "aventura"],
    keywords: ["palenque", "agua azul", "misol"],
    description: {
      es: "Ruinas mayas envueltas en selva, cascadas turquesa y biodiversidad.",
      en: "Maya ruins wrapped in jungle, turquoise waterfalls and biodiversity.",
    },
  },
  {
    slug: "guanajuato",
    name: "Guanajuato",
    state: "Guanajuato",
    lat: 21.02,
    lng: -101.26,
    citySlug: "guanajuato",
    image: destGuanajuato,
    categories: ["cultura", "gastronomia", "romance"],
    keywords: ["guanajuato", "san miguel de allende"],
    description: {
      es: "Callejones coloridos, callejoneadas y patrimonio colonial vivo.",
      en: "Colorful alleys, callejoneadas and living colonial heritage.",
    },
  },
  {
    slug: "san-miguel-de-allende",
    name: "San Miguel de Allende",
    state: "Guanajuato",
    lat: 20.91,
    lng: -100.74,
    citySlug: "san-miguel-de-allende",
    image: destSMA,
    categories: ["cultura", "gastronomia", "romance"],
    keywords: ["san miguel de allende", "san miguel allende"],
    description: {
      es: "Pueblo Mágico colonial, cuna de la Independencia y refugio creativo.",
      en: "Colonial Pueblo Mágico, cradle of Independence and creative haven.",
    },
  },
  {
    slug: "teotihuacan",
    name: "Teotihuacán",
    state: "Estado de México",
    lat: 19.69,
    lng: -98.84,
    citySlug: "teotihuacan",
    image: destTeotihuacan,
    categories: ["arqueologia", "cultura", "familiar"],
    keywords: ["teotihuacan", "teotihuacán", "piramides del sol", "pirámides del sol"],
    description: {
      es: "La 'Ciudad de los Dioses' con las pirámides del Sol y la Luna.",
      en: "The 'City of the Gods' with the pyramids of the Sun and Moon.",
    },
  },
  {
    slug: "izamal",
    name: "Izamal",
    state: "Yucatán",
    lat: 20.93,
    lng: -89.02,
    citySlug: "izamal",
    image: destIzamal,
    categories: ["cultura", "cultura-maya"],
    keywords: ["izamal", "ciudad amarilla"],
    description: {
      es: "La Ciudad Amarilla, convento franciscano sobre basamento maya.",
      en: "The Yellow City, a Franciscan convent built atop a Maya platform.",
    },
  },
  {
    slug: "mani",
    name: "Maní",
    state: "Yucatán",
    lat: 20.39,
    lng: -89.39,
    citySlug: "mani",
    image: destMani,
    categories: ["cultura", "cultura-maya", "gastronomia"],
    keywords: ["mani", "maní"],
    description: {
      es: "Cuna del poc chuc y escenario del auto de fe de Fray Diego de Landa.",
      en: "Birthplace of poc chuc and site of Fray Diego de Landa's auto-da-fé.",
    },
  },
  {
    slug: "sisal",
    name: "Sisal",
    state: "Yucatán",
    lat: 21.16,
    lng: -90.04,
    citySlug: "sisal",
    image: destSisal,
    categories: ["playa", "naturaleza", "familiar"],
    keywords: ["sisal"],
    description: {
      es: "Puerto histórico Pueblo Mágico con manglares, flamencos y mar tranquilo.",
      en: "Historic harbor and Pueblo Mágico with mangroves, flamingos and calm sea.",
    },
  },
  {
    slug: "celestun",
    name: "Celestún",
    state: "Yucatán",
    lat: 20.86,
    lng: -90.39,
    citySlug: "celestun",
    image: destCelestun,
    categories: ["naturaleza", "playa", "familiar"],
    keywords: ["celestun", "celestún"],
    description: {
      es: "Reserva de la biosfera con miles de flamencos rosados en el estuario.",
      en: "Biosphere reserve home to thousands of pink flamingos in the estuary.",
    },
  },
  {
    slug: "rio-lagartos",
    name: "Río Lagartos",
    state: "Yucatán",
    lat: 21.59,
    lng: -88.16,
    citySlug: "rio-lagartos",
    image: destRioLagartos,
    categories: ["naturaleza", "aventura"],
    keywords: ["rio lagartos", "río lagartos"],
    description: {
      es: "Reserva natural de flamencos, cocodrilos y baños de arcilla maya.",
      en: "Natural reserve of flamingos, crocodiles and Maya clay baths.",
    },
  },
  {
    slug: "las-coloradas",
    name: "Las Coloradas",
    state: "Yucatán",
    lat: 21.61,
    lng: -87.99,
    citySlug: "las-coloradas",
    image: destLasColoradas,
    categories: ["naturaleza"],
    keywords: ["las coloradas", "lagunas rosas"],
    description: {
      es: "Las famosas lagunas rosadas teñidas por microorganismos halófilos.",
      en: "The famous pink lagoons colored by halophilic microorganisms.",
    },
  },
  {
    slug: "el-cuyo",
    name: "El Cuyo",
    state: "Yucatán",
    lat: 21.51,
    lng: -87.68,
    citySlug: "el-cuyo",
    image: destElCuyo,
    categories: ["playa", "naturaleza", "aventura"],
    keywords: ["el cuyo"],
    description: {
      es: "Playa salvaje sin multitudes, paraíso del kitesurf en la Reserva de Ría Lagartos.",
      en: "Wild uncrowded beach, kitesurf paradise inside the Ría Lagartos Reserve.",
    },
  },
  {
    slug: "mahahual",
    name: "Mahahual",
    state: "Quintana Roo",
    lat: 18.71,
    lng: -87.71,
    citySlug: "mahahual",
    image: destMahahual,
    categories: ["playa", "naturaleza", "aventura"],
    keywords: ["mahahual", "majahual", "costa maya"],
    description: {
      es: "Pueblo de pescadores frente a la Gran Barrera Mesoamericana.",
      en: "Fishing village facing the Mesoamerican Reef System.",
    },
  },
  {
    slug: "calakmul",
    name: "Calakmul",
    state: "Campeche",
    lat: 18.10,
    lng: -89.81,
    citySlug: "calakmul",
    image: destCalakmul,
    categories: ["arqueologia", "naturaleza", "cultura-maya", "aventura"],
    keywords: ["calakmul"],
    description: {
      es: "Ciudad maya monumental rodeada de la segunda selva más grande de América.",
      en: "Monumental Maya city surrounded by the second largest jungle in the Americas.",
    },
  },
  {
    slug: "xpujil",
    name: "Xpujil",
    state: "Campeche",
    lat: 18.50,
    lng: -89.40,
    citySlug: "xpujil",
    image: destXpujil,
    categories: ["arqueologia", "cultura-maya", "aventura"],
    keywords: ["xpujil", "xpuhil"],
    description: {
      es: "Puerta a la región Río Bec, con templos mayas envueltos en selva.",
      en: "Gateway to the Río Bec region, with Maya temples wrapped in jungle.",
    },
  },
];

export const EXPLORA_CATEGORIES: ExploraCategory[] = [
  "playa",
  "cultura",
  "naturaleza",
  "aventura",
  "islas",
  "arqueologia",
  "gastronomia",
  "cenotes",
  "cultura-maya",
  "vida-nocturna",
  "familiar",
  "romance",
];

/** Lowercase + strip diacritics. */
function fold(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

interface TourLike {
  title: string;
  location: string | null;
  summary: string | null;
}

/** Count tours from the unified catalog that match a destination. */
export function countToursForDestination(
  dest: ExploraDestination,
  tours: TourLike[],
): number {
  let count = 0;
  for (const t of tours) {
    const hay = fold(`${t.title} ${t.location ?? ""} ${t.summary ?? ""}`);
    if (dest.keywords.some((k) => hay.includes(fold(k)))) count += 1;
  }
  return count;
}

/** Return the tours from the catalog that match a destination's keywords. */
export function findToursForDestination<T extends TourLike>(
  dest: ExploraDestination,
  tours: T[],
): T[] {
  return tours.filter((t) => {
    const hay = fold(`${t.title} ${t.location ?? ""} ${t.summary ?? ""}`);
    return dest.keywords.some((k) => hay.includes(fold(k)));
  });
}

/** Category-level keyword reinforcement (independent of destination match). */
const CATEGORY_KEYWORDS: Record<ExploraCategory, string[]> = {
  playa: ["playa", "beach", "snorkel", "catamaran", "catamarán"],
  cultura: ["cultura", "museo", "pueblo magico", "pueblo mágico", "colonial", "centro historico", "centro histórico"],
  naturaleza: ["naturaleza", "reserva", "manglar", "flamingo", "flamenco", "selva", "biosfera"],
  aventura: ["aventura", "tirolesa", "zipline", "atv", "rafting", "kayak", "buggy", "jungla"],
  islas: ["isla", "island"],
  arqueologia: ["arqueolog", "ruinas", "piramide", "pirámide", "zona arqueol"],
  gastronomia: ["gastronom", "cocina", "comida", "mezcal", "tequila", "tasting", "degustacion", "degustación", "food", "culinari"],
  cenotes: ["cenote"],
  "cultura-maya": ["maya"],
  "vida-nocturna": ["noche", "nocturna", "rooftop", "club", "bar"],
  familiar: ["familia", "family", "kids"],
  romance: ["romantic", "romántico", "luna de miel", "sunset", "atardecer"],
};

/**
 * Return tours that match a category, by either:
 * - matching a destination whose categories include the requested category, OR
 * - containing category-level reinforcement keywords in title/location/summary.
 */
export function findToursForCategory<T extends TourLike>(
  category: ExploraCategory,
  tours: T[],
): T[] {
  const matchingDests = EXPLORA_DESTINATIONS.filter((d) => d.categories.includes(category));
  const destKeywords = new Set<string>();
  for (const d of matchingDests) for (const k of d.keywords) destKeywords.add(fold(k));
  const catKeywords = (CATEGORY_KEYWORDS[category] || []).map(fold);
  const out: T[] = [];
  const seen = new Set<string | T>();
  for (const t of tours) {
    const hay = fold(`${t.title} ${t.location ?? ""} ${t.summary ?? ""}`);
    const hit =
      catKeywords.some((k) => hay.includes(k)) ||
      [...destKeywords].some((k) => hay.includes(k));
    if (hit && !seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  }
  return out;
}

/** Project lng/lat to the SVG viewBox used by MexicoMap (1000x580). */
export const MAP_VIEWBOX = { width: 1000, height: 580 } as const;
const LNG_MIN = -118.5;
const LNG_MAX = -86.0;
const LAT_MIN = 14.0;
const LAT_MAX = 33.0;

export function projectToMap(lng: number, lat: number): { x: number; y: number } {
  const x = ((lng - LNG_MIN) / (LNG_MAX - LNG_MIN)) * MAP_VIEWBOX.width;
  const y = ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * MAP_VIEWBOX.height;
  return { x, y };
}
